import * as React from 'react';
import { Title } from '../../utils/title';
import { fetchApi } from '../../services/api';
import { FieldWrapper } from '@progress/kendo-react-form';
import { Input, InputSuffix, TextArea } from '@progress/kendo-react-inputs';
import { MultiSelect } from '@progress/kendo-react-dropdowns';
import { Button } from '@progress/kendo-react-buttons';
import { Popover, PopoverActionsBar } from '@progress/kendo-react-tooltip';
import { Loader } from '@progress/kendo-react-indicators';
import { eyeIcon } from '@progress/kendo-svg-icons';
import { dateFormatter } from 'utils/utils';

type IDNameViewModel = {
  ID: number;
  Name: string;
};

type TargetsResponse = {
  Locations: IDNameViewModel[];
  Teams: IDNameViewModel[];
};

type CreateAnnouncementCommand = {
  Text: string;
  Body: string;
  PopupLink: string;
  ForLocationIDs: number[];
  ForTeamIDs: number[];
};

type AnnouncementHistoryItem = {
  Id: string;
  Text: string;
  Body: string;
  PopupLink: string | null;
  CreatedDateTime: string;
  ForLocationID: number | null;
  ForTeamID: number | null;
  Dismissed: boolean;
  DismissedAt: string | null;
  AcknowledgedCount: number;
  CreatedByFullName: string | null;
};

type AnnouncementHistoryResponse = {
  Announcements: AnnouncementHistoryItem[];
};

type AnnouncementAcknowledgementItem = {
  UserID: string;
  FullName: string | null;
  DismissedAt: string;
};

type AnnouncementAcknowledgementsResponse = {
  Acknowledgements: AnnouncementAcknowledgementItem[];
};

const allLocationsOption: IDNameViewModel = { ID: 0, Name: 'All Locations' };
const allTeamsOption: IDNameViewModel = { ID: 0, Name: 'All Teams' };

const AnnouncementCreate: React.FC = () => {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [historyRefreshing, setHistoryRefreshing] = React.useState(false);
  const [history, setHistory] = React.useState<AnnouncementHistoryItem[]>([]);
  const [ackPopoverOpen, setAckPopoverOpen] = React.useState(false);
  const [ackLoading, setAckLoading] = React.useState(false);
  const [ackAnnouncement, setAckAnnouncement] = React.useState<AnnouncementHistoryItem | null>(null);
  const [acknowledgements, setAcknowledgements] = React.useState<AnnouncementAcknowledgementItem[]>([]);
  const [ackAnchor, setAckAnchor] = React.useState<HTMLElement | null>(null);
  const [locations, setLocations] = React.useState<IDNameViewModel[]>([]);
  const [teams, setTeams] = React.useState<IDNameViewModel[]>([]);
  const [command, setCommand] = React.useState<CreateAnnouncementCommand>({
    Text: '',
    Body: '',
    PopupLink: '',
    ForLocationIDs: [],
    ForTeamIDs: [],
  });

  const loadHistory = React.useCallback(async (options?: { showSpinner?: boolean }) => {
    const showSpinner = options?.showSpinner ?? true;
    if (showSpinner) setHistoryRefreshing(true);
    try {
      const response = await fetchApi('/api/Account/AnnouncementHistory') as AnnouncementHistoryResponse;
      setHistory(response.Announcements || []);
    } catch (error) {
      console.error('Failed to load announcement history', error);
      alert('Unable to load announcement history.');
    } finally {
      if (showSpinner) setHistoryRefreshing(false);
    }
  }, []);

  React.useEffect(() => {
    const loadInitial = async () => {
      setLoading(true);
      const [targetsResult, historyResult] = await Promise.allSettled([
        fetchApi('/api/Account/NotificationTargets') as Promise<TargetsResponse>,
        fetchApi('/api/Account/AnnouncementHistory') as Promise<AnnouncementHistoryResponse>,
      ]);

      const errors: string[] = [];

      if (targetsResult.status === 'fulfilled') {
        setLocations([...(targetsResult.value.Locations || [])]);
        setTeams([...(targetsResult.value.Teams || [])]);
      } else {
        console.error('Failed to load notification targets', targetsResult.reason);
        errors.push('locations/teams');
      }

      if (historyResult.status === 'fulfilled') {
        setHistory(historyResult.value.Announcements || []);
      } else {
        console.error('Failed to load announcement history', historyResult.reason);
        errors.push('announcement history');
      }

      if (errors.length > 0) {
        alert(`Unable to load ${errors.join(' and ')}.`);
      }

      setLoading(false);
    };

    loadInitial();
  }, [loadHistory]);

  const updateCommand = (updates: Partial<CreateAnnouncementCommand>) => {
    setCommand((prev) => ({ ...prev, ...updates }));
  };

  const dismissAnnouncement = async (announcement: AnnouncementHistoryItem) => {
    if (announcement.Dismissed) return;
    const ok = window.confirm(`Hide "${announcement.Text}" for everyone?`);
    if (!ok) return;

    try {
      await fetchApi(`/api/Account/AnnouncementDismiss/${announcement.Id}`, {}, 'POST');
      setHistory((prev) => prev.map((item) => (
        item.Id === announcement.Id
          ? { ...item, Dismissed: true, DismissedAt: new Date().toISOString() }
          : item
      )));
    } catch (error) {
      console.error('Failed to hide announcement', error);
      alert('Unable to hide announcement.');
    }
  };

  const closeAcknowledgements = () => {
    setAckPopoverOpen(false);
    setAckAnchor(null);
  };

  const openAcknowledgements = async (announcement: AnnouncementHistoryItem, anchorEl: HTMLElement) => {
    if (ackPopoverOpen && ackAnnouncement?.Id === announcement.Id)
    {
      closeAcknowledgements();
      return;
    }

    setAckAnnouncement(announcement);
    setAckAnchor(anchorEl);
    setAckPopoverOpen(true);
    setAckLoading(true);
    setAcknowledgements([]);
    try {
      const response = await fetchApi(`/api/Account/AnnouncementAcknowledgements/${announcement.Id}`) as AnnouncementAcknowledgementsResponse;
      setAcknowledgements(response.Acknowledgements || []);
    } catch (error) {
      console.error('Failed to load announcement acknowledgements', error);
      alert('Unable to load announcement acknowledgements.');
    } finally {
      setAckLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await fetchApi('/api/Account/Announcement', {
        ...command,
        Text: command.Text,
        Body: command.Body,
        PopupLink: command.PopupLink,
        ForLocationIDs: command.ForLocationIDs.filter((id) => id > 0),
        ForTeamIDs: command.ForTeamIDs.filter((id) => id > 0),
      }, 'POST');
      setCommand({
        Text: '',
        Body: '',
        PopupLink: '',
        ForLocationIDs: [],
        ForTeamIDs: [],
      });
      loadHistory();
    } catch (error) {
      console.error('Failed to send announcement', error);
      alert('Unable to send announcement.');
    } finally {
      setSaving(false);
    }
  };

  const locationName = (locationId: number | null) => {
    if (!locationId) return allLocationsOption.Name;
    return locations.find((x) => x.ID === locationId)?.Name || `N/A`;
  };

  const teamName = (teamId: number | null) => {
    if (!teamId) return allTeamsOption.Name;
    return teams.find((x) => x.ID === teamId)?.Name || `N/A`;
  };

  return (
    <div className="container mt-3">
      <Title string="Send Announcement" />
      {loading && <div className="text-center my-3"><Loader type="converging-spinner" /></div>}
      {!loading && (
        <div className="k-form k-form-md p-0">
          <FieldWrapper>
            <Input
              required
              autoFocus
              value={command.Text}
              onChange={(e) => updateCommand({ Text: e.value })}
              maxLength={250}
              placeholder="Announcement title"
            />
          </FieldWrapper>
          <FieldWrapper>
            <TextArea
              required
              rows={6}
              value={command.Body}
              onChange={(e) => updateCommand({ Body: e.target.value })}
              placeholder="Announcement details"
            />
          </FieldWrapper>
          <FieldWrapper>
            <MultiSelect
              label="Location"
              data={locations}
              textField="Name"
              dataItemKey="ID"
              value={locations.filter((x) => command.ForLocationIDs.includes(x.ID))}
              onChange={(e) => updateCommand({ ForLocationIDs: (e.value as IDNameViewModel[]).map((x) => x.ID) })}
              placeholder={allLocationsOption.Name}
            />
          </FieldWrapper>
          <FieldWrapper>
            <MultiSelect
              label="Team"
              data={teams}
              textField="Name"
              dataItemKey="ID"
              value={teams.filter((x) => command.ForTeamIDs.includes(x.ID))}
              onChange={(e) => updateCommand({ ForTeamIDs: (e.value as IDNameViewModel[]).map((x) => x.ID) })}
              placeholder={allTeamsOption.Name}
              suffix={() => <InputSuffix orientation="horizontal">
                <Button fillMode='flat' onClick={(e) => {
                  e.stopPropagation();
                  updateCommand({
                    ForLocationIDs: [4],
                    ForTeamIDs: teams.filter((x) => [1, 2, 3, 4, 5, 6, 7, 9, 10, 11, 13].includes(x.ID)).map((x) => x.ID)
                  });
                }}>
                  Taylor Ops
                </Button>
              </InputSuffix>}
            />
          </FieldWrapper>
          <FieldWrapper>
            <Input
              label="Link (optional)"
              type='url'
              value={command.PopupLink}
              onChange={(e) => updateCommand({ PopupLink: e.value })}
              placeholder="https://..."
            />
          </FieldWrapper>
          <div className="d-flex mt-3 justify-content-end">
            <Button
              themeColor="primary"
              onClick={save}
              disabled={saving}
            >
              {saving ? 'Sending...' : 'Send Announcement'}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-4">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h5 className="mb-0">Announcement History</h5>
        </div>

        {historyRefreshing && (
          <div className="text-center my-3"><Loader type="converging-spinner" /></div>
        )}

        {!historyRefreshing && history.length === 0 && (
          <div className="text-muted">No announcements found.</div>
        )}

        {!historyRefreshing && history.length > 0 && (
          <div>
            {history.map((item) => (
              <div className="card mb-3" key={item.Id}>
                <div className="card-header d-flex align-items-center justify-content-between">
                  <div className="flex-shrink-0 pr-2">
                    <Button
                      svgIcon={eyeIcon}
                      onClick={(e) => openAcknowledgements(item, e.currentTarget)}
                    >
                      {item.AcknowledgedCount || 0}
                    </Button>
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-semibold">{item.Text}</div>
                    <div className="small text-muted">
                      {dateFormatter(item.CreatedDateTime)} · {item.CreatedByFullName || 'Unknown'}
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <div className="mb-2">{item.Body}</div>
                  {item.PopupLink && <a href={item.PopupLink} target='_blank' rel="noopener noreferrer">{item.PopupLink}</a>}
                </div>
                <div className="card-footer d-flex align-items-center justify-content-between">
                  <div>
                    {locationName(item.ForLocationID)} &bull; {teamName(item.ForTeamID)}
                  </div>
                  {!item.Dismissed ? <Button
                    themeColor="warning"
                    size='small'
                    onClick={() => dismissAnnouncement(item)}
                    disabled={item.Dismissed}
                  >
                    Hide
                  </Button> : <>Hidden at {dateFormatter(item.DismissedAt)}</>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Popover
        show={ackPopoverOpen}
        onClose={() => closeAcknowledgements()}
        anchor={ackAnchor}
        position="bottom"
        contentStyle={{ width: 420 }}
      >
        {ackLoading && (
          <div className="text-center my-3"><Loader type="converging-spinner" /></div>
        )}

        {!ackLoading && acknowledgements.length === 0 && (
          <div className="text-muted">No one has acknowledged this yet.</div>
        )}

        {!ackLoading && acknowledgements.length > 0 && (
          <div className="list-group" style={{ maxHeight: 300, overflowY: 'auto' }}>
            {acknowledgements.map((x) => (
              <div className="list-group-item d-flex justify-content-between" key={x.UserID + x.DismissedAt}>
                <div>{x.FullName || x.UserID}</div>
                <div className="text-muted">{dateFormatter(x.DismissedAt)}</div>
              </div>
            ))}
          </div>
        )}
        <PopoverActionsBar>
          <Button onClick={() => closeAcknowledgements()}>Close</Button>
        </PopoverActionsBar>
      </Popover>
    </div>
  );
};

export default AnnouncementCreate;
